import { getAccessToken } from '@accenturecodefoundry/mfe-pot-shared-auth/core';
import { JobBankApiClient } from './job-bank-api-client';
import { JobApplication, JobPosting } from './models';

/** Local dev / integration tests: calls the real job-bank-bff over HTTP. */
export class HttpJobBankApiClient implements JobBankApiClient {
  constructor(private readonly baseUrl: string) {}

  /**
   * Attaches the mock-idp-issued bearer token (if signed in) so
   * job-bank-bff can independently verify it -- see mfe-pot's plan doc.
   * Nothing here decodes the token itself; it's forwarded as an opaque
   * credential.
   */
  private authHeaders(extra?: Record<string, string>): HeadersInit {
    const token = getAccessToken();
    return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }

  async getPostings(): Promise<JobPosting[]> {
    const response = await fetch(`${this.baseUrl}/api/v1/jobs`, { headers: this.authHeaders() });
    if (!response.ok) {
      throw new Error(`job-bank-bff returned ${response.status} for /api/v1/jobs`);
    }
    return (await response.json()) as JobPosting[];
  }

  async apply(jobId: string, applicantSub: string): Promise<JobApplication> {
    const response = await fetch(`${this.baseUrl}/api/v1/applications`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ jobId, applicantSub }),
    });
    if (!response.ok) {
      throw new Error(`job-bank-bff returned ${response.status} for POST /api/v1/applications`);
    }
    return (await response.json()) as JobApplication;
  }

  async getApplications(applicantSub: string): Promise<JobApplication[]> {
    const url = new URL(`${this.baseUrl}/api/v1/applications`);
    url.searchParams.set('applicantSub', applicantSub);
    const response = await fetch(url, { headers: this.authHeaders() });
    if (!response.ok) {
      throw new Error(`job-bank-bff returned ${response.status} for /api/v1/applications`);
    }
    return (await response.json()) as JobApplication[];
  }
}
